/** Acronyms kept capitalized when sentence-casing report text. */
export const SENTENCE_CASE_ACRONYMS = [
  'AR',
  'CPA',
  'LTV',
  'NPS',
  'ROI',
  'SRP',
  'SRPs',
] as const

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function restoreAcronyms(text: string, acronyms: readonly string[]): string {
  if (acronyms.length === 0) return text
  const acronymPattern = acronyms.map(escapeRegExp).join('|')
  const acronymByLowercase = new Map(
    acronyms.map((acronym) => [acronym.toLowerCase(), acronym]),
  )
  return text.replace(
    new RegExp(`\\b(?:${acronymPattern})\\b`, 'gi'),
    (match) => acronymByLowercase.get(match.toLowerCase()) ?? match,
  )
}

/** Lowercase text and restore known acronyms, without capitalizing a new sentence. */
export function toLowerCaseKeepingAcronyms(
  text: string,
  acronyms: readonly string[] = SENTENCE_CASE_ACRONYMS,
): string {
  return restoreAcronyms(text.toLowerCase(), acronyms)
}

/**
 * Lowercase the text, capitalize its first alphabetical character, and
 * restore known acronyms. Pass a custom acronym list for context-specific
 * terms that should remain fully capitalized.
 */
export function toSentenceCase(
  text: string,
  acronyms: readonly string[] = SENTENCE_CASE_ACRONYMS,
): string {
  const lower = text.toLowerCase()
  const firstLetterIndex = lower.search(/[a-z]/)

  if (firstLetterIndex === -1) return lower

  const sentenceCase =
    lower.slice(0, firstLetterIndex) +
    lower.charAt(firstLetterIndex).toUpperCase() +
    lower.slice(firstLetterIndex + 1)

  return restoreAcronyms(sentenceCase, acronyms)
}

export type ReportTitleFormatState = {
  titled: boolean
  insideParen: boolean
}

/** Sentence-case a title fragment. Text inside parentheses stays lowercase. */
export function formatReportTitleFragment(
  text: string,
  state: ReportTitleFormatState,
): string {
  let result = ''
  let index = 0
  while (index < text.length) {
    if (!state.insideParen) {
      const open = text.indexOf('(', index)
      const chunk = text.slice(index, open === -1 ? text.length : open)
      if (!state.titled && /[A-Za-z]/.test(chunk)) {
        result += toSentenceCase(chunk)
        state.titled = true
      } else if (/[A-Za-z]/.test(chunk)) {
        result += toLowerCaseKeepingAcronyms(chunk)
      } else {
        result += chunk
      }
      if (open === -1) break
      result += '('
      state.insideParen = true
      index = open + 1
      continue
    }
    const close = text.indexOf(')', index)
    const chunk = text.slice(index, close === -1 ? text.length : close)
    result += toLowerCaseKeepingAcronyms(chunk)
    if (close === -1) break
    result += ')'
    state.insideParen = false
    index = close + 1
  }
  return result
}

/** Sentence-case a report chart or table section title, keeping report acronyms. */
export function toReportSectionTitle(text: string): string {
  return formatReportTitleFragment(text, { titled: false, insideParen: false })
}

/** Normalize compact thousands suffixes to uppercase (`$628.4k` → `$628.4K`). */
export function toKpiValueDisplay(value: string): string {
  return value.replace(/(\d)k(?=$|[^A-Za-z])/gi, '$1K')
}

/** Sentence-case prose. Leave values that start with a number or symbol unchanged. */
export function toSentenceCaseProse(
  text: string,
  acronyms: readonly string[] = SENTENCE_CASE_ACRONYMS,
): string {
  if (!/^[A-Za-z]/.test(text.trimStart())) return text
  return toSentenceCase(text, acronyms)
}
